import React from 'react';
import { 
  VStack, 
  HStack,
  Text, 
  Card, 
  CardBody, 
  Icon, 
  Badge, 
  Button,
  Box,
  Divider,
  SimpleGrid,
  useColorModeValue,
  Flex
} from '@chakra-ui/react';
import { 
  FiCheckCircle, 
  FiClock, 
  FiMail, 
  FiPhone, 
  FiFileText,
  FiUsers,
  FiDollarSign,
  FiShield,
  FiHome
} from 'react-icons/fi';
import { RegistrationData } from '../index';

interface RegistrationCompleteProps {
  data: RegistrationData;
  updateData: (data: RegistrationData) => void;
  onNext: () => void;
  onPrevious: () => void;
}

const RegistrationComplete: React.FC<RegistrationCompleteProps> = ({ data }) => {
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const celebrationBg = useColorModeValue('green.50', 'green.900');
  const completedItemBg = useColorModeValue('green.50', 'green.900');
  const nextStepBg = useColorModeValue('blue.50', 'blue.900');
  const actionBg = useColorModeValue('gray.50', 'gray.700');

  const completedItems = [
    {
      icon: FiHome,
      title: 'Company Information',
      description: 'Business details and contact information',
      completed: true
    },
    {
      icon: FiUsers,
      title: 'Key Contacts',
      description: 'Department contacts for operations',
      completed: true
    },
    {
      icon: FiDollarSign,
      title: 'Payment & Banking',
      description: 'Payment terms and banking setup',
      completed: true
    },
    {
      icon: FiFileText,
      title: 'Document Signatures',
      description: 'W-9, ACH authorization, and agreements',
      completed: true
    },
    {
      icon: FiShield,
      title: 'Supporting Documents',
      description: 'Business licenses and certifications',
      completed: true
    }
  ];

  const nextSteps = [
    {
      step: '1',
      title: 'Document Review',
      description: 'Our team will review your submitted documents and information',
      timeline: '1-2 business days'
    },
    {
      step: '2',
      title: 'Account Verification',
      description: 'Banking details and business credentials will be verified',
      timeline: '2-3 business days'
    },
    {
      step: '3',
      title: 'Account Activation',
      description: 'Your vendor account will be activated and you can begin receiving orders',
      timeline: '3-5 business days'
    }
  ];

  return (
    <VStack spacing={8} align="stretch">
      {/* Celebration Header */}
      <Card bg={celebrationBg} borderColor="green.200" borderWidth={2}>
        <CardBody p={12}>
          <VStack spacing={6} align="center">
            <Flex
              w={20}
              h={20}
              bg="green.500"
              borderRadius="full"
              align="center"
              justify="center"
            >
              <Icon as={FiCheckCircle} color="white" boxSize={10} />
            </Flex>
            
            <VStack spacing={3} align="center">
              <Text fontSize="3xl" fontWeight="bold" color="green.700" textAlign="center">
                Registration Complete!
              </Text>
              <Text fontSize="lg" color="green.600" textAlign="center">
                Welcome to our vendor network. Your application has been successfully submitted.
              </Text>
            </VStack>

            <HStack spacing={4}>
              <Badge colorScheme="green" size="lg" px={4} py={2}>
                Application Submitted
              </Badge>
              <Badge colorScheme="blue" variant="outline" size="lg" px={4} py={2}>
                Review in Progress
              </Badge>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Registration Summary */}
      <Card>
        <CardBody p={8}>
          <VStack spacing={6} align="stretch">
            <Text fontSize="lg" fontWeight="semibold">
              Registration Summary
            </Text>
            
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              {completedItems.map((item, index) => (
                <HStack key={index} spacing={4} p={4} bg={completedItemBg} borderRadius="lg">
                  <Flex
                    w={10}
                    h={10}
                    bg="green.500"
                    borderRadius="full"
                    align="center"
                    justify="center"
                  >
                    <Icon as={item.icon} color="white" boxSize={5} />
                  </Flex>
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="semibold" color="green.700">
                      {item.title}
                    </Text>
                    <Text fontSize="sm" color="green.600">
                      {item.description}
                    </Text>
                  </VStack>
                  <Icon as={FiCheckCircle} color="green.500" boxSize={5} ml="auto" />
                </HStack>
              ))}
            </SimpleGrid>
          </VStack>
        </CardBody>
      </Card>

      {/* What Happens Next */}
      <Card>
        <CardBody p={8}>
          <VStack spacing={6} align="stretch">
            <HStack spacing={3}>
              <Icon as={FiClock} color="blue.500" boxSize={5} />
              <Text fontSize="lg" fontWeight="semibold">
                What Happens Next
              </Text>
            </HStack>

            <VStack spacing={6} align="stretch">
              {nextSteps.map((step, index) => (
                <Box key={index}>
                  <HStack spacing={4} align="start">
                    <Flex
                      w={8}
                      h={8}
                      bg="blue.500"
                      borderRadius="full"
                      align="center"
                      justify="center"
                      flexShrink={0}
                    >
                      <Text color="white" fontWeight="bold" fontSize="sm">
                        {step.step}
                      </Text>
                    </Flex>
                    <VStack align="start" spacing={2} flex={1}>
                      <HStack justify="space-between" w="full">
                        <Text fontWeight="semibold">
                          {step.title}
                        </Text>
                        <Badge colorScheme="blue" variant="outline">
                          {step.timeline}
                        </Badge>
                      </HStack>
                      <Text color="gray.600" fontSize="sm">
                        {step.description}
                      </Text>
                    </VStack>
                  </HStack>
                  {index < nextSteps.length - 1 && (
                    <Box ml={4} mt={3} mb={3}>
                      <Divider orientation="vertical" h={6} borderColor="blue.200" />
                    </Box>
                  )}
                </Box>
              ))}
            </VStack>

            <Card bg={nextStepBg} borderColor="blue.200">
              <CardBody>
                <HStack spacing={3}>
                  <Icon as={FiMail} color="blue.500" />
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="semibold" color="blue.700">
                      Email Notifications
                    </Text>
                    <Text fontSize="sm" color="blue.600">
                      We'll send updates to <strong>{data.companyInfo?.email}</strong> as your application progresses through each step.
                    </Text>
                  </VStack>
                </HStack>
              </CardBody>
            </Card>
          </VStack>
        </CardBody>
      </Card>

      {/* Contact & Support */}
      <Card>
        <CardBody p={8}>
          <VStack spacing={6} align="stretch">
            <Text fontSize="lg" fontWeight="semibold">
              Need Help or Have Questions?
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <Card borderColor={borderColor} borderWidth={1}>
                <CardBody p={6}>
                  <VStack spacing={4} align="center">
                    <Icon as={FiMail} color="green.500" boxSize={8} />
                    <VStack spacing={2} align="center">
                      <Text fontWeight="semibold">
                        Email Support
                      </Text>
                      <Text fontSize="sm" color="gray.600" textAlign="center">
                        General questions about your application
                      </Text>
                      <Button
                        as="a"
                        href="mailto:vendors@company.com"
                        colorScheme="green"
                        variant="outline"
                        size="sm"
                      >
                        vendors@company.com
                      </Button>
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>

              <Card borderColor={borderColor} borderWidth={1}>
                <CardBody p={6}>
                  <VStack spacing={4} align="center">
                    <Icon as={FiPhone} color="blue.500" boxSize={8} />
                    <VStack spacing={2} align="center">
                      <Text fontWeight="semibold">
                        Phone Support
                      </Text>
                      <Text fontSize="sm" color="gray.600" textAlign="center">
                        Urgent issues or application status
                      </Text>
                      <Button
                        as="a"
                        href="tel:+1-555-0123"
                        colorScheme="blue"
                        variant="outline"
                        size="sm"
                      >
                        (555) 012-3456
                      </Button>
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>
            </SimpleGrid>

            <Text fontSize="sm" color="gray.600" textAlign="center">
              <strong>Business Hours:</strong> Monday - Friday, 8:00 AM - 6:00 PM EST
            </Text>
          </VStack>
        </CardBody>
      </Card>

      {/* Action Buttons */}
      <Card bg={actionBg}>
        <CardBody p={8}>
          <VStack spacing={4} align="center">
            <Text fontWeight="semibold" color="gray.700">
              What would you like to do next?
            </Text>
            
            <HStack spacing={4} wrap="wrap" justify="center">
              <Button
                colorScheme="blue"
                size="lg"
                leftIcon={<Icon as={FiFileText} />}
                onClick={() => window.print()}
              >
                Print Summary
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                leftIcon={<Icon as={FiMail} />}
                as="a"
                href="mailto:vendors@company.com?subject=Vendor Registration - Application Submitted"
              >
                Contact Support
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                onClick={() => window.location.href = '/'}
              >
                Return to Home
              </Button>
            </HStack>
          </VStack>
        </CardBody>
      </Card>
    </VStack>
  );
};

export default RegistrationComplete; 